/**
 * API response types for the v4 dashboard.
 * All shapes derived from live endpoint inspection (Phase 0, 2026-05-14).
 * Each interface cites the endpoint it comes from.
 * canonical: S0 — lines 578–721
 */

// ── /api/reports/stats ─────────────────────────────────────────────────────
/** GET /api/reports/stats → {ok, data: DashboardStats} */
export interface DashboardStats {
  projects:         number
  payapps:          number
  total_billed:     number
  outstanding:      number
  collected_mtd:    number
  stripe_connected: boolean
}

// ── /api/projects ──────────────────────────────────────────────────────────
/** GET /api/projects → Project[] (flat array, no .data wrapper) */
export interface Project {
  id:                number
  user_id:           number
  name:              string | null
  number:            string | null
  owner:             string | null
  owner_id?:         number | null
  owner_email:       string | null
  owner_phone:       string | null
  contractor:        string | null
  architect:         string | null
  original_contract: string | null  // numeric stored as string from pg
  status:            ProjectStatus
  created_at:        string
  pay_app_count:     number
  /** Added by PR-V4-DASHBOARD subquery */
  total_billed:      string          // numeric(14,2) → string from pg
  /** Pulse G702 line 4 on latest posted PA */
  pulse_billed?:     string | number
  pulse_held?:       string | number
  pulse_collected?:  string | number
  pulse_open?:       string | number
  /** Added by PR-V4-DASHBOARD subquery */
  outstanding:       string          // numeric(14,2) → string from pg
  /** Latest submitted non-RR pay app cumulative retainage snapshot */
  retention_held:    string | null   // numeric(14,2) → string from pg
  payments_received?: string | null  // numeric(14,2) → string from pg
  change_order_total?: string | null // numeric(14,2) → string from pg
  default_retainage: number | null
  payment_terms:     string | null
  /** GC contact name — optional, may be null */
  contact:           string | null
  /** Excluded from billing sync when true */
  is_test_project?: boolean
  exclude_from_forecast?: boolean
  /** Job / substantial completion date (TIMESTAMPTZ; DATE semantics in UI) */
  completed_at?: string | null
}

export type ProjectStatus = 'active' | 'completed' | 'draft' | string

// ── /api/aria/follow-up-queue ──────────────────────────────────────────────
export type FollowUpTone = 'gentle' | 'firm' | 'final'

export interface FollowUpItem {
  pay_app_id:   number
  project_id:   number
  project_name: string
  amount_due:   number
  days_overdue: number
  tone:         FollowUpTone
  app_number?:  number
  owner_email?: string | null
  follow_up_recipient_email?: string | null
  follow_up_recipient_name?: string | null
  owner_contact_name?: string | null
  last_reminder_sent_at?: string | null
}

export interface FollowUpQueueResponse {
  queue: FollowUpItem[]
}

export interface RetentionAlert {
  pay_app_id: number
  app_number: number
  project_id: number
  project_name: string
  default_retainage: number
  below_default_count: number
}

export interface RetentionAlertsResponse {
  alerts: RetentionAlert[]
}

// ── /api/aria/lien-alerts ──────────────────────────────────────────────────
export interface LienAlert {
  id:            number
  project_id:    number
  project_name:  string
  deadline_date: string   // ISO date
  filed_at:      string | null
  days_remaining: number
  /** e.g. "pre_lien" | "mechanics_lien" | "retention_release" */
  notice_type:    string | null
}

export interface LienAlertsResponse {
  alerts: LienAlert[]
}

// ── /api/ai/ask ────────────────────────────────────────────────────────────
export interface AskAriaRequest {
  question: string
}

export interface AskAriaResponse {
  answer:       string
  questionType: string
}

// ── /api/projects/:id/hub/trades ──────────────────────────────────────────
/**
 * GET /api/projects/:id/hub/trades → { data: ProjectTrade[] }
 * canonical: S2 — lines 1563–1600 (sub rows in modal Tab 1)
 */
export type TradeStatus = 'active' | 'invited' | 'inactive' | string

export interface ProjectTrade {
  id:               number          // project_vendors.id (invite row)
  project_trade_id: number | null   // project_trades.id — canonical hub_uploads.trade_id
  project_id:       number
  name:             string          // trade name e.g. "Plumbing"
  company_name:     string | null
  contact_name:     string | null
  contact_email:    string | null
  magic_link_token: string | null
  email_alias:      string | null
  status:           TradeStatus
  invite_sent_at:   string | null
  created_at:       string
  vendor_user_id:   number | null   // resolved via email JOIN
  vendor_id?:       number | null
}

// ── /api/projects/:id/hub/inbox ───────────────────────────────────────────
/**
 * GET /api/projects/:id/hub/inbox → { data: HubInboxItem[] }
 * canonical: S2 — lines 1601–1636 (pm-ibx section in modal Tab 1)
 * Also used filtered by trade_id for Sub Detail nested view (lines 1718–1752)
 */
export type HubDocType =
  | 'invoice'
  | 'lien_waiver'
  | 'insurance'
  | 'w9'
  | 'change_order'
  | 'other'
  | string

export type HubUploadStatus = 'pending' | 'approved' | 'rejected' | 'changes_requested' | string

/** PATCH /api/hub/:projectId/uploads/:uploadId/review */
export type HubReviewAction = 'approved' | 'rejected' | 'changes_requested' | 'paid'

export type HubRejectionType =
  | 'missing_retention'
  | 'insurance_expired'
  | 'missing_lien_waiver'
  | 'format_error'
  | 'other'

export interface ReviewUploadBody {
  action:          HubReviewAction
  comment?:        string
  rejection_type?: HubRejectionType
  amount?:         number
  payment_method?: string
  payment_reference?: string
}

export interface ReviewUploadResponse {
  data:           HubInboxItem
  error:          string | null
  message:        string | null
  reupload_slot?: HubInboxItem
}

export interface HubInboxItem {
  id:            number
  project_id:    number
  trade_id:      number | null
  filename:      string
  original_name: string
  file_size:     number
  mime_type:     string | null
  doc_type:      HubDocType
  amount:        string | null      // numeric as string from pg
  source:        string | null
  uploaded_by:   string | null
  uploaded_by_role?: string | null
  notes:         string | null
  status:        HubUploadStatus
  created_at:    string
  deleted_at?:   string | null
  insurance_expires?: string | null
  paid_at?:      string | null
  paid_amount?:  string | null      // numeric as string from pg
  paid_method?:  string | null
  paid_reference?: string | null
  // JOINed from project_trades
  trade_name:    string | null      // pt.name
  company_name:  string | null      // pt.company_name
}

export interface SubPaymentsRow {
  sub:         string
  invoiced:    number
  approved:    number
  paid:        number
  outstanding: number
  invoices: {
    id: number
    original_name: string
    amount: string | null
    status: string
    created_at: string
    approved_at: string | null
    paid_at: string | null
    paid_amount: string | null
  }[]
}

// ── /api/projects/:id/payapps ─────────────────────────────────────────────
/**
 * GET /api/projects/:id/payapps → PayApp[] (flat array)
 * canonical: S2 — lines 1871–1902 (Tab 3 pay applications table)
 */
export type PayAppStatus = 'draft' | 'submitted' | 'paid' | string
export type PaymentStatus = 'unpaid' | 'partial' | 'paid' | 'processing' | string

export interface PayApp {
  id:                     number
  project_id:             number
  app_number:             number
  period_label:           string | null
  period_start:           string | null
  period_end:             string | null
  po_number?:             string | null
  special_notes?:         string | null
  status:                 PayAppStatus
  amount_due:             string    // numeric(14,2) as string from pg
  retention_held:         string    // numeric(14,2) as string from pg
  retention_this_period?: string | null // numeric(14,2) as string from pg
  is_retainage_release:   boolean
  payment_status:         PaymentStatus
  amount_paid:            string    // numeric(14,2) as string from pg
  submitted_at:           string | null
  created_at:             string | null
  deleted_at:             string | null
  // LATERAL join: last manual payment info
  last_payment_method:    string | null
  last_payment_date?:   string | null
  last_check_number:      string | null
  last_payment_amount:    string | null
}

// ── /api/projects/:id/lien-docs ───────────────────────────────────────────
/**
 * GET /api/projects/:id/lien-docs → LienDoc[] (flat array)
 * canonical: S2 — lines 1667–1705 (Tab 2 lien panel)
 */
export type LienDocType = 'pre_lien' | 'mechanics_lien' | 'lien_release' | string

export interface LienDoc {
  id:           number
  project_id:   number
  pay_app_id:   number | null
  doc_type:     LienDocType
  filename:     string
  created_at:   string
}

// ── /api/aria/lien-alerts?project_id=<id> ────────────────────────────────
/**
 * LienAlert is already defined above (portfolio-level).
 * The same endpoint filtered by project_id returns the same shape.
 * Re-export alias for clarity in project-scoped contexts.
 */
// LienAlert is defined above — import directly from './types' where needed

// ── HubInbox response wrapper ─────────────────────────────────────────────
export interface HubInboxResponse {
  data: HubInboxItem[]
}

// ── Hub trades response wrapper ───────────────────────────────────────────
export interface HubTradesResponse {
  data: ProjectTrade[]
}

// ═══════════════ PR-V4-NEW-PROJECT-WIZARD (canonical: S1 lines 722–1302) ════════

/** GET /api/settings → ContractorSettings; canonical: S1 line 771 */
export interface ContractorSettings {
  company_name:          string | null
  default_payment_terms: string | null
  default_retainage:     number | null
  contact_name:          string | null
  contact_phone:         string | null
  contact_email:         string | null
  company_address:       string | null
  company_city:          string | null
  company_state:         string | null
  company_zip:           string | null
  license_number:        string | null
  logo_filename:         string | null
  signature_filename:    string | null
}

/** POST /api/projects body; canonical: S1 lines 756–800 */
export interface CreateProjectInput {
  name:              string
  number:            string | null
  owner:             string
  owner_id?:         number | null
  owner_email:       string | null
  owner_phone:       string | null
  contractor:        string | null
  architect:         string | null
  contact:           string | null
  contact_name:      string | null
  contact_phone:     string | null
  contact_email:     string | null
  building_area:     string | null
  original_contract: number | null
  contract_date:     string | null
  est_date:          string | null
  default_retainage: number
  payment_terms:     string | null
  include_architect: boolean
  include_retainage: boolean
  jurisdiction:      string | null
  address?:          string | null
}

/** SOV line item from parser; canonical: S1 lines 802–834 */
export interface SOVImportOption {
  name:         string
  amount:       number
  recommended?: boolean
}

export interface SOVLineItem {
  item_id:         string
  description:     string
  scheduled_value: number
  amount_label?:   string | null
  note?:           string | null
  is_parent?:      boolean
  /** Bid option / alternate line — excluded from the SOV unless the user opts it in. */
  is_option?:      boolean
  kind?:           'base' | 'allowance' | 'alternate' | 'excluded' | 'tbd' | 'scope' | string
  /** Multi-option proposals: which priced option(s) this line belongs to. */
  applies_to?:     string[]
  /** Client-side: row currently excluded from totals + save. */
  excluded?:       boolean
  /** Client-side: raw amount text while editing (format on blur only). */
  amount_draft?:   string
}

export interface SOVImportSection {
  name:  string
  items: SOVLineItem[]
}

export interface SOVImportNote {
  description: string
  kind:        string
  note?:       string
}

export interface SOVChecksum {
  matches:       boolean
  delta:         number
  items_sum:     number
  stated_total:  number | null
}

/** POST /api/sov/parse response; canonical: S1 lines 802–834 */
export interface SOVParseResult {
  rows:           SOVLineItem[]
  all_rows:       SOVLineItem[]
  row_count:      number
  total_rows:     number
  computed_total: number
  reported_total: number | null
  summary:        Record<string, number>
  filename:       string
  sheet_used:     string
  warning?:       string | null
  sections?:              SOVImportSection[]
  import_notes?:          SOVImportNote[]
  checksum?:              SOVChecksum
  skipped_summary_count?: number
  options?:               SOVImportOption[]
  selected_option?:       string
  import_stage?:          string
  source_temp_id?:        string | null
}

/** POST /api/projects/:id/sov body; canonical: S1 lines 836–915 */
export interface SaveSOVInput {
  lines: Array<{
    item_id: string
    description: string
    scheduled_value: number
    amount_label?: string | null
    sort_order: number
  }>
  source_temp_id?: string | null
}

/** POST /api/projects/:id/payapps body; canonical: S1 lines 917–944 */
export interface CreatePayAppInput {
  period_label: string | null
  period_start: string | null
  period_end:   string | null
  po_number?: string | null
  special_notes?: string | null
}

/** Pay app line (from GET /api/payapps/:id .lines); canonical: S1 lines 945–1000 */
export interface PayAppLine {
  id:               number
  pay_app_id:       number
  sov_line_id:      number
  item_id:          string
  description:      string
  scheduled_value:  string
  prev_pct:         string
  this_pct:         string
  retainage_pct:    string
  stored_materials: string
  sort_order:       number
}

/** PUT /api/payapps/:id/lines body; canonical: S1 lines 945–1000 */
export interface UpdatePayAppLinesInput {
  lines: Array<{ id: number; this_pct: number; retainage_pct: number; stored_materials: number }>
}

/** GET /api/payapps/:id full response; canonical: S1 lines 917–1000 */
export interface PayAppDetail {
  id:                number
  project_id:        number
  app_number:        number
  period_label:      string | null
  period_start:      string | null
  period_end:        string | null
  status:            PayAppStatus
  amount_due:        string
  retention_held:    string
  retention_this_period?: string | null
  payment_status?:   PaymentStatus
  submitted_at?:     string | null
  po_number:         string | null
  special_notes:     string | null
  notes:             string | null
  created_at:        string
  project_name:      string
  owner:             string | null
  owner_email:       string | null
  contractor:        string | null
  original_contract: string | null
  contract_date:     string | null
  payment_terms:     string | null
  lines:             PayAppLine[]
  change_orders:     ChangeOrder[]
  attachments:       PayAppAttachment[]
  scheduled_sum?:    number | string | null
  retention_notice_dismissed_at?: string | null
}

/** Change order (forward-compat for PR-V4-PAY-APP-FULL); canonical: S1 lines 1001–1062 */
export type ChangeOrderStatus = 'pending' | 'approved' | 'void' | 'voided' | string
export interface ChangeOrder {
  id: number; pay_app_id: number; co_number: number
  description: string; amount: string; status: ChangeOrderStatus; created_at: string
}

/** Attachment (forward-compat for PR-V4-PAY-APP-FULL); canonical: S1 lines 1063–1098 */
export interface PayAppAttachment {
  id: number; pay_app_id: number; filename: string
  original_name: string | null; file_size: number | null
  mime_type: string | null; uploaded_at: string
  change_order_id?: number | null
}

/** POST /api/projects/:id/notice-drafts body; canonical: S1 lines 1252–1279 */
export interface NoticeDraftRecipient { name: string; address: string; role: string }
export interface CreateNoticeDraftInput {
  state:         string
  recipients:    NoticeDraftRecipient[]
  notice_type:   string
  deadline_date: string | null
}

/** notice_drafts DB row; canonical: S1 lines 1252–1279 */
export interface NoticeDraft {
  id: number; project_id: number; state: string
  recipients: NoticeDraftRecipient[] | string
  notice_type: string; deadline_date: string | null
  status: string; created_at: string; updated_at: string
}

/** sessionStorage key v4_wizard_in_progress */
export const WIZARD_SCHEMA_VERSION = 2

/** Fields the user explicitly edited this session — settings auto-fill skips these. */
export type Step1DirtyFields = Partial<Record<
  'contractor' | 'default_retainage' | 'payment_terms'
  | 'contact_name' | 'contact_phone' | 'contact_email',
  boolean
>>

export interface WizardInProgress {
  schemaVersion?: number
  step: number; sub?: number
  projectId?: number; payAppId?: number
  step1: Partial<CreateProjectInput>
  step1Dirty?: Step1DirtyFields
  step2Lines: SOVLineItem[]
  step2FileName?: string | null
  step2Import?: {
    sections?:              SOVImportSection[]
    import_notes?:          SOVImportNote[]
    checksum?:              SOVChecksum
    skipped_summary_count?: number
    options?:               SOVImportOption[]
    selected_option?:       string
    all_rows?:              SOVLineItem[]
    source_temp_id?:        string | null
    source_file_name?:      string | null
  }
  step4Sub1?: {
    period_label: string | null
    period_start: string | null
    period_end: string | null
    po_number?: string | null
    special_notes?: string | null
  }
  /** G703 grid draft percentages — preserved on wizard back-nav before Save & Continue */
  step4G703Draft?: Array<{ id: number; _thisPct: string; _retPct: string; _retTouched?: boolean }>
  step5: Partial<CreateNoticeDraftInput>
}
// ── /api/contractor/vendors ───────────────────────────────────────────────
/**
 * GET /api/contractor/vendors → ContractorVendor[]
 * Vendor address book entry; used by InviteSubModal.
 * canonical: PR-V4-SUB-PORTAL
 */
export interface ContractorVendor {
  id:           number
  company_name: string
  contact_name: string | null
  email:        string
  phone:        string | null
  trade:        string | null
}

// ── /api/contractor/projects/:id/vendors ─────────────────────────────────────
/**
 * GET /api/contractor/projects/:id/vendors → ProjectVendorRow[]
 * Raw shape returned by the contractor-vendors endpoint (v4 invite flow).
 * Used by fetchProjectVendors() to populate SubsTab.
 * canonical: PR-V4-VENDOR-ACCEPT-INVITE
 */
export interface ProjectVendorRow {
  project_vendor_id: number
  project_trade_id:  number | null
  status:            string
  invited_at:        string
  vendor_id:         number
  company_name:      string
  contact_name:      string | null
  email:             string
  phone:             string | null
  trade:             string | null
  vendor_user_id:    number | null
  invite_token:      string | null
  invite_accepted_at: string | null
  invite_expires_at:  string | null
}
